'use client'

import type { ComponentProps, ReactNode } from 'react'

import { Avatar as AvatarPrimitive } from '@base-ui/react/avatar'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'

import {
    AiSparkleIcon,
    ArrowSquareOutIcon,
    ChatDoneIcon,
    ChatOpenIcon,
    CaretDownIcon,
    CheckCircleIcon,
    ClockIcon,
    EnvelopeSimpleIcon,
    FlowArrowIcon,
    GearSixIcon,
    GlobeIcon,
    HeartIcon,
    MessengerLogoIcon,
    PaperPlaneTiltIcon,
    RocketIcon,
    ThumbsDownIcon,
    ThumbsUpIcon,
    UserHeartIcon,
    UserIcon,
    UserLightningIcon,
    XIcon
} from '@/components/icons/icon-set'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from '@/components/ui/tooltip'

/*
 * GoodUnited / Chat. Figma: 10 / Components / Chat (Supporter Response).
 * The building blocks of the Supporter Response inbox: contact avatars and rows, tags, message bubbles,
 * journey links, timeline events, the reply composer with its channel select and footer, and contact details.
 * Colors come from Colors/chat (bubbles, the selected row, tags and the AI accent) plus the shared tokens.
 */

/* Chat/ContactAvatar: a 40px avatar on Colors/surface/muted with a 2px raised ring, and the mission and high-intent indicators above it. */
interface ContactAvatarProps extends Omit<ComponentProps<'span'>, 'children'> {
    initials: string
    src?: string
    mission?: boolean
    intent?: boolean
}

function ContactAvatar({ initials, src, mission = true, intent = true, className, ...props }: ContactAvatarProps) {
    const indicators = [mission && 'shared a mission statement', intent && 'high intent'].filter(Boolean).join(', ')

    return (
        <span data-slot='contact-avatar' className={cn('relative inline-flex size-10 shrink-0', className)} {...props}>
            <Avatar size='xl' className='border-2 border-surface-raised bg-surface-muted text-text-primary'>
                {src && <AvatarImage src={src} alt='' />}
                <AvatarFallback className='text-sm font-medium'>
                    {initials}
                </AvatarFallback>
            </Avatar>
            {indicators && (
                <span
                    role='img'
                    aria-label={indicators}
                    className='absolute -top-2 left-5 inline-flex h-4.5 items-center gap-0.5 rounded-full border border-border-default bg-surface-raised p-0.5 text-icon-primary [&_svg]:size-3'
                >
                    {mission && <HeartIcon />}
                    {intent && <RocketIcon />}
                </span>
            )}
        </span>
    )
}

/*
 * Chat/ContactPhoto: the supporter's photo as a small print, 96px wide in a 4:5 frame, tilted 2° to the left, so the
 * profile reads as a person rather than a record. A 4px Colors/surface/raised frame with a Colors/border/subtle edge,
 * 12px outer and 8px inner corners, and shadow-photo. Without a photo, or until it loads, the initials sit on
 * Colors/avatar in Poppins / text-2xl / semibold.
 */
interface ContactPhotoProps extends Omit<ComponentProps<'span'>, 'children'> {
    initials: string
    src?: string
    alt?: string
}

function ContactPhoto({ initials, src, alt = '', className, ...props }: ContactPhotoProps) {
    return (
        <span
            data-slot='contact-photo'
            className={cn('inline-flex w-24 shrink-0 -rotate-2 rounded-xl border border-border-subtle bg-surface-raised p-1 shadow-photo', className)}
            {...props}
        >
            <AvatarPrimitive.Root className='flex aspect-4/5 w-full items-center justify-center overflow-hidden rounded-lg bg-avatar-background text-avatar-foreground select-none'>
                {src && <AvatarPrimitive.Image src={src} alt={alt} className='size-full object-cover' />}
                <AvatarPrimitive.Fallback className='font-heading text-2xl font-semibold'>
                    {initials}
                </AvatarPrimitive.Fallback>
            </AvatarPrimitive.Root>
        </span>
    )
}

/* Chat/Tag: a supporter tag on Colors/chat/tag/background with its category dot. Suggested tags carry the dashed AI border. */
interface ChatTagProps extends Omit<ComponentProps<'span'>, 'children'> {
    label: string
    kind?: 'default' | 'suggested'
    onRemove?: () => void
    onSelect?: () => void
}

function ChatTag({ label, kind = 'default', onRemove, onSelect, className, ...props }: ChatTagProps) {
    const content = (
        <>
            <span className='size-2 shrink-0 rounded-full bg-chat-tag-dot' aria-hidden='true' />
            <span className='truncate'>
                {label}
            </span>
        </>
    )

    return (
        <span
            data-slot='chat-tag'
            data-kind={kind}
            className={cn(
                'inline-flex max-w-full items-center gap-1.5 rounded-full bg-chat-tag px-2 py-1 text-sm text-text-primary',
                kind === 'suggested' && 'border border-dashed border-chat-ai',
                className
            )}
            {...props}
        >
            {onSelect
                ? (
                    <button
                        type='button'
                        onClick={onSelect}
                        aria-label={`Add ${label}`}
                        className='inline-flex min-w-0 cursor-pointer items-center gap-1.5 rounded-full outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus'
                    >
                        {content}
                    </button>
                )
                : content}
            {onRemove && (
                <button
                    type='button'
                    onClick={onRemove}
                    aria-label={`Remove ${label}`}
                    className='inline-flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full text-icon-primary outline-none hover:bg-surface-raised focus-visible:outline-2 focus-visible:outline-border-focus [&_svg]:size-3'
                >
                    <XIcon aria-hidden='true' />
                </button>
            )}
        </span>
    )
}

/* Chat/MessageBubble: incoming on Colors/chat/bubble/incoming with a border, outgoing on Colors/chat/bubble/outgoing. Body stays Roboto 16 / 24. */
interface MessageBubbleProps extends ComponentProps<'div'> {
    direction?: 'incoming' | 'outgoing'
}

function MessageBubble({ direction = 'incoming', className, ...props }: MessageBubbleProps) {
    return (
        <div
            data-slot='message-bubble'
            data-direction={direction}
            className={cn(
                'rounded-2xl p-4 text-base whitespace-pre-line text-text-primary',
                direction === 'incoming' ? 'border border-border-default bg-chat-bubble-incoming' : 'rounded-tl-sm bg-chat-bubble-outgoing',
                className
            )}
            {...props}
        />
    )
}

/* Chat/JourneyLink: the journey a message came from, as a raised pill with the flow arrow. */
interface JourneyLinkProps extends Omit<ComponentProps<'span'>, 'children'> {
    label: string
}

function JourneyLink({ label, className, ...props }: JourneyLinkProps) {
    return (
        <span
            data-slot='journey-link'
            className={cn('inline-flex h-7.5 max-w-full shrink-0 items-center gap-1.5 rounded-full border border-border-default bg-surface-raised px-2 text-sm text-text-primary', className)}
            {...props}
        >
            <FlowArrowIcon className='size-3.5 shrink-0 text-icon-secondary' aria-hidden='true' />
            <span className='truncate'>
                {label}
            </span>
        </span>
    )
}

/* Chat/ContactGroupHeader: a date group in the contact list with its count. */
interface ContactGroupHeaderProps extends Omit<ComponentProps<'div'>, 'children'> {
    label: string
    count?: number
}

function ContactGroupHeader({ label, count, className, ...props }: ContactGroupHeaderProps) {
    return (
        <div
            data-slot='contact-group-header'
            className={cn('flex h-12 shrink-0 items-center gap-2 bg-surface-subtle p-3 text-sm text-text-secondary', className)}
            {...props}
        >
            <span className='min-w-0 flex-1 truncate'>
                {label}
            </span>
            {count !== undefined && (
                <span className='shrink-0 text-right tabular-nums'>
                    {count}
                </span>
            )}
        </div>
    )
}

type ContactRowState = 'default' | 'unread' | 'selected' | 'done'

/*
 * Chat/ContactRow: a two-line row with unread, selected and done states. The whole row is one button. With
 * onToggleDone, a 36px square button appears at the right on hover or keyboard focus: the chat bubble with a check
 * marks the conversation as done, the bubble with lines marks it as undone. It sits beside the row button, never in it.
 */
interface ContactRowProps extends Omit<ComponentProps<'button'>, 'children'> {
    initials: string
    src?: string
    summary: ReactNode
    preview: ReactNode
    time?: string
    state?: ContactRowState
    mission?: boolean
    intent?: boolean
    done?: boolean
    onToggleDone?: () => void
}

function ContactRow({ done = false, onToggleDone, className, ...props }: ContactRowProps) {
    if (!onToggleDone) return <ContactRowButton className={className} {...props} />

    const label = done ? 'Mark as undone' : 'Mark as done'

    return (
        <div data-slot='contact-row-frame' className='group/row relative shrink-0'>
            <ContactRowButton className={cn('group-hover/row:pr-16 group-has-[:focus-visible]/row:pr-16', className)} {...props} />
            <Tooltip>
                <TooltipTrigger
                    render={(
                        <button
                            type='button'
                            data-slot='contact-row-done'
                            aria-label={label}
                            onClick={onToggleDone}
                            className='absolute top-1/2 right-4 inline-flex size-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md border border-border-default bg-surface-raised text-icon-primary pointer-events-none opacity-0 transition-opacity outline-none group-hover/row:pointer-events-auto group-hover/row:opacity-100 focus-visible:pointer-events-auto group-has-[:focus-visible]/row:opacity-100 hover:bg-surface-subtle focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-border-focus [&_svg]:size-5'
                        />
                    )}
                >
                    {done ? <ChatOpenIcon /> : <ChatDoneIcon />}
                </TooltipTrigger>
                <TooltipContent side='left'>
                    {label}
                </TooltipContent>
            </Tooltip>
        </div>
    )
}

function ContactRowButton({ initials, src, summary, preview, time, state = 'default', mission = true, intent = true, className, ...props }: Omit<ContactRowProps, 'done' | 'onToggleDone'>) {
    return (
        <button
            type='button'
            data-slot='contact-row'
            data-state={state}
            aria-current={state === 'selected' ? 'true' : undefined}
            className={cn(
                'relative flex w-full shrink-0 cursor-pointer items-center gap-3 border-b border-border-subtle p-4 text-left transition-colors outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-border-focus',
                state === 'selected' && 'bg-chat-row-selected',
                state === 'done' && 'bg-surface-subtle hover:bg-surface-muted',
                (state === 'default' || state === 'unread') && 'bg-surface-raised hover:bg-surface-subtle',
                className
            )}
            {...props}
        >
            <ContactAvatar initials={initials} src={src} mission={mission} intent={intent} />
            <span className='flex min-w-0 flex-1 flex-col gap-1'>
                <span className='flex h-6 items-center gap-2'>
                    <span className={cn('min-w-0 flex-1 truncate text-sm', state === 'unread' ? 'font-medium text-text-primary' : state === 'done' ? 'text-text-secondary' : 'text-text-primary')}>
                        {summary}
                    </span>
                    {time && (
                        <span className='shrink-0 text-right text-xs text-text-secondary'>
                            {time}
                        </span>
                    )}
                </span>
                <span className={cn('truncate text-base', state === 'done' ? 'text-text-tertiary' : 'text-text-primary')}>
                    {preview}
                </span>
            </span>
            {state === 'unread' && (
                <span className='size-1.5 shrink-0 rounded-full bg-chat-tag-dot'>
                    <span className='sr-only'>Unread</span>
                </span>
            )}
        </button>
    )
}

type TimelineEventKind = 'waiting' | 'ai-draft' | 'replied' | 'email-replied' | 'done'

const timelineIcons: Record<TimelineEventKind, ReactNode> = {
    'waiting': <ClockIcon className='text-icon-tertiary' />,
    'ai-draft': <AiSparkleIcon className='text-icon-primary' />,
    'replied': <MessengerLogoIcon className='text-icon-secondary' />,
    'email-replied': <EnvelopeSimpleIcon className='text-icon-secondary' />,
    'done': <CheckCircleIcon className='text-status-success-foreground' />
}

/*
 * Chat/ShareIndicators: what the supporter shared, as overlapping 28px circles on Colors/surface/raised with a
 * Colors/border/default edge: Stay Involved (UserLightning) then Mission Story (UserHeart).
 */
interface ShareIndicatorsProps extends Omit<ComponentProps<'span'>, 'children'> {
    mission?: boolean
    involvement?: boolean
}

function ShareIndicators({ mission = true, involvement = true, className, ...props }: ShareIndicatorsProps) {
    const label = [involvement && 'shared how they want to stay involved', mission && 'shared a mission story'].filter(Boolean).join(', ')
    if (!label) return null

    return (
        <span data-slot='share-indicators' role='img' aria-label={label} className={cn('inline-flex shrink-0 items-center -space-x-1.5', className)} {...props}>
            {involvement && (
                <span className='inline-flex size-7 items-center justify-center rounded-full border border-border-default bg-surface-raised text-icon-primary'>
                    <UserLightningIcon className='size-4' />
                </span>
            )}
            {mission && (
                <span className='inline-flex size-7 items-center justify-center rounded-full border border-border-default bg-surface-raised text-icon-primary'>
                    <UserHeartIcon className='size-4' />
                </span>
            )}
        </span>
    )
}

/*
 * Chat/TimelineEvent: one status line in the conversation. The icon sits in a 24px circle on Colors/surface/raised
 * with a Colors/border/default edge, on the thread line, so the line runs into it. name reads in Colors/text/primary,
 * the rest in Colors/text/secondary; trailing holds ShareIndicators or similar.
 */
interface TimelineEventProps extends ComponentProps<'div'> {
    kind: TimelineEventKind
    name?: string
    trailing?: ReactNode
}

function TimelineEvent({ kind, name, trailing, className, children, ...props }: TimelineEventProps) {
    return (
        <div
            data-slot='timeline-event'
            data-kind={kind}
            className={cn('relative flex min-h-7 items-center gap-3 text-base text-text-secondary', className)}
            {...props}
        >
            <span className='inline-flex size-6 shrink-0 items-center justify-center rounded-full border border-border-default bg-surface-raised [&_svg]:size-4 [&_svg]:shrink-0' aria-hidden='true'>
                {timelineIcons[kind]}
            </span>
            <span className='min-w-0'>
                {name && (
                    <span className='text-text-primary'>
                        {`${name} `}
                    </span>
                )}
                {children}
            </span>
            {trailing}
        </div>
    )
}

/*
 * Chat/ThreadMessage: who shared what and in which journey, with the message bubble indented below. The sender's
 * 24px avatar (initials, or src) starts the thread line: a 1px Colors/border/default line runs down the 24px gutter
 * to the next message or event, and a curved branch with an 8px corner turns from it into the bubble. name reads in
 * Colors/text/primary, the context in Colors/text/secondary. Without initials, a person icon in a circle stands in.
 */
interface ThreadMessageProps extends Omit<ComponentProps<'div'>, 'children'> {
    context: ReactNode
    name?: string
    initials?: string
    src?: string
    journey?: string
    children: ReactNode
}

function ThreadMessage({ context, name, initials, src, journey, className, children, ...props }: ThreadMessageProps) {
    return (
        <div data-slot='thread-message' className={cn('group/thread relative flex flex-col gap-3', className)} {...props}>
            <span aria-hidden='true' className='absolute top-6 -bottom-6 left-3 w-px bg-border-default group-last/thread:bottom-0' />
            <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
                {initials
                    ? (
                        <Avatar size='sm' className='bg-surface-muted text-text-primary'>
                            {src && <AvatarImage src={src} alt='' />}
                            <AvatarFallback className='text-xs font-medium'>
                                {initials}
                            </AvatarFallback>
                        </Avatar>
                    )
                    : (
                        <span className='inline-flex size-6 shrink-0 items-center justify-center rounded-full border border-border-default bg-surface-raised text-icon-secondary' aria-hidden='true'>
                            <UserIcon className='size-4' />
                        </span>
                    )}
                <span className='min-w-0 flex-1 text-base text-text-secondary'>
                    {name && (
                        <span className='text-text-primary'>
                            {`${name} `}
                        </span>
                    )}
                    {context}
                </span>
                {journey && <JourneyLink label={journey} className='ml-9 sm:ml-0' />}
            </div>
            <div className='relative pl-9'>
                <span aria-hidden='true' className='absolute bottom-7 left-3 h-4 w-6 rounded-bl-lg border-b border-l border-border-default' />
                {children}
            </div>
        </div>
    )
}

type ReplyChannel = 'messenger' | 'email'

const channelOptions: { value: ReplyChannel, label: string, icon: ReactNode }[] = [
    { value: 'messenger', label: 'Messenger', icon: <MessengerLogoIcon /> },
    { value: 'email', label: 'Email', icon: <EnvelopeSimpleIcon /> }
]

/* Chat/ChannelSelect: Messenger or email for the reply, opened as a menu. */
interface ChannelSelectProps {
    value: ReplyChannel
    onValueChange: (value: ReplyChannel) => void
    className?: string
}

function ChannelSelect({ value, onValueChange, className }: ChannelSelectProps) {
    const current = channelOptions.find(option => option.value === value) ?? channelOptions[0]

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                render={<Button variant='ghost' size='sm' className={cn('h-9 px-2', className)} aria-label={`Reply channel: ${current.label}`} />}
            >
                {current.icon}
                {current.label}
                <CaretDownIcon className='size-3' aria-hidden='true' />
            </DropdownMenuTrigger>
            <DropdownMenuContent className='w-50 min-w-50 p-1'>
                {channelOptions.map(option => (
                    <DropdownMenuItem key={option.value} onClick={() => onValueChange(option.value)} aria-current={option.value === value ? 'true' : undefined}>
                        {option.icon}
                        {option.label}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

/*
 * Chat/ReplyComposer: the drafted reply in a frame with the composer gradient border (chat-composer-frame).
 * channel messenger ends with Send; email adds the subject and ends with Copy and open email.
 */
interface ReplyComposerProps extends Omit<ComponentProps<'div'>, 'children' | 'onSubmit'> {
    channel: ReplyChannel
    onChannelChange: (channel: ReplyChannel) => void
    message: string
    onMessageChange: (message: string) => void
    subject?: string
    onSubjectChange?: (subject: string) => void
    onSend?: () => void
    onCopyEmail?: () => void
    label?: string
}

function ReplyComposer({ channel, onChannelChange, message, onMessageChange, subject = '', onSubjectChange, onSend, onCopyEmail, label = 'Reply', className, ...props }: ReplyComposerProps) {
    const empty = !message.trim()

    return (
        <div
            data-slot='reply-composer'
            data-channel={channel}
            className={cn('chat-composer-frame flex flex-col gap-4 rounded-lg p-4 focus-within:outline-1 focus-within:outline-offset-2 focus-within:outline-border-focus', className)}
            {...props}
        >
            {channel === 'email' && (
                <label className='flex items-baseline gap-2'>
                    <span className='w-14 shrink-0 text-sm text-text-secondary'>
                        Subject:
                    </span>
                    <input
                        value={subject}
                        onChange={event => onSubjectChange?.(event.target.value)}
                        className='min-w-0 flex-1 bg-transparent text-base text-text-primary outline-none placeholder:text-text-placeholder'
                        placeholder='Add a subject'
                    />
                </label>
            )}
            <Textarea
                value={message}
                onChange={event => onMessageChange(event.target.value)}
                aria-label={label}
                placeholder='Write a reply'
                className='min-h-30 resize-none rounded-none border-0 bg-transparent p-0 text-text-placeholder hover:border-0 focus-visible:border-0 focus-visible:text-text-primary'
            />
            <div className='flex items-center gap-2'>
                <ChannelSelect value={channel} onValueChange={onChannelChange} />
                <span className='flex-1' />
                {channel === 'messenger'
                    ? (
                        <Button size='sm' onClick={onSend} disabled={empty}>
                            Send
                            <PaperPlaneTiltIcon aria-hidden='true' />
                        </Button>
                    )
                    : (
                        <Button size='sm' onClick={onCopyEmail} disabled={empty}>
                            Copy and open email
                            <ArrowSquareOutIcon aria-hidden='true' />
                        </Button>
                    )}
            </div>
        </div>
    )
}

type ReplyFeedback = 'up' | 'down' | null

/* Chat/ComposerFooter: Set up your writing style, then thumbs up and down feedback on the draft. */
interface ComposerFooterProps extends Omit<ComponentProps<'div'>, 'children'> {
    onWritingStyle?: () => void
    feedback?: ReplyFeedback
    onFeedback?: (feedback: ReplyFeedback) => void
}

function ComposerFooter({ onWritingStyle, feedback = null, onFeedback, className, ...props }: ComposerFooterProps) {
    return (
        <div data-slot='composer-footer' className={cn('flex min-h-14 items-center gap-2 bg-surface-subtle p-2', className)} {...props}>
            <Button variant='ghost' size='sm' onClick={onWritingStyle}>
                <GearSixIcon aria-hidden='true' />
                Set up your writing style
            </Button>
            <span className='flex-1' />
            <Button
                variant='outline'
                size='icon-sm'
                aria-label='Good draft'
                aria-pressed={feedback === 'up'}
                onClick={() => onFeedback?.(feedback === 'up' ? null : 'up')}
            >
                <ThumbsUpIcon weight={feedback === 'up' ? 'fill' : 'regular'} aria-hidden='true' />
            </Button>
            <Button
                variant='outline'
                size='icon-sm'
                aria-label='Poor draft'
                aria-pressed={feedback === 'down'}
                onClick={() => onFeedback?.(feedback === 'down' ? null : 'down')}
            >
                <ThumbsDownIcon weight={feedback === 'down' ? 'fill' : 'regular'} aria-hidden='true' />
            </Button>
        </div>
    )
}

/* Chat/SentNotice: the guard shown once a reply went out, with Write another. */
interface SentNoticeProps extends Omit<ComponentProps<'div'>, 'children'> {
    message?: string
    onWriteAnother?: () => void
}

function SentNotice({ message = 'A message has already been sent. Would you like to write another one?', onWriteAnother, className, ...props }: SentNoticeProps) {
    return (
        <div
            data-slot='sent-notice'
            className={cn('flex flex-wrap items-center gap-4 rounded-lg border border-border-default bg-surface-subtle p-4', className)}
            {...props}
        >
            <p className='min-w-48 flex-1 text-sm text-text-secondary'>
                {message}
            </p>
            <Button variant='outline' size='sm' onClick={onWriteAnother}>
                Write another
            </Button>
        </div>
    )
}

type ContactDetailKind = 'location' | 'messenger' | 'email'

const detailIcons: Record<ContactDetailKind, { icon: ReactNode, label: string }> = {
    location: { icon: <GlobeIcon />, label: 'Location' },
    messenger: { icon: <MessengerLogoIcon />, label: 'Messenger' },
    email: { icon: <EnvelopeSimpleIcon />, label: 'Email' }
}

/* Chat/ContactDetail: one line of the supporter profile, location, Messenger name or email. */
interface ContactDetailProps extends ComponentProps<'div'> {
    kind: ContactDetailKind
}

function ContactDetail({ kind, className, children, ...props }: ContactDetailProps) {
    return (
        <div data-slot='contact-detail' data-kind={kind} className={cn('flex min-h-6 items-center gap-2 text-sm text-text-primary', className)} {...props}>
            <span className='inline-flex shrink-0 text-icon-secondary [&_svg]:size-4' aria-hidden='true'>
                {detailIcons[kind].icon}
            </span>
            <span className='sr-only'>
                {`${detailIcons[kind].label}: `}
            </span>
            <span className='min-w-0 break-words'>
                {children}
            </span>
        </div>
    )
}

export {
    ChannelSelect,
    ChatTag,
    ComposerFooter,
    ContactAvatar,
    ContactDetail,
    ContactGroupHeader,
    ContactPhoto,
    ContactRow,
    JourneyLink,
    MessageBubble,
    ReplyComposer,
    SentNotice,
    ShareIndicators,
    ThreadMessage,
    TimelineEvent
}
export type { ContactDetailKind, ContactRowState, ReplyChannel, ReplyFeedback, TimelineEventKind }
